import React, { useState, useEffect } from 'react';
import { inventoryService } from '../../services/api';
import SearchableDropdown from '../common/SearchableDropdown';

interface Category {
  id: number;
  name: string;
  description: string;
  parent_id?: number;
  parent_name?: string;
  is_active: boolean;
}

interface CategoryFormProps {
  category?: Category;
  onSave: (categoryData: any) => void;
  onCancel: () => void;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  resetForm?: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({ 
  category, 
  onSave, 
  onCancel, 
  isCollapsed, 
  onToggleCollapse, 
  resetForm
}) => {
  const [formData, setFormData] = useState({
    name: category?.name || '',
    description: category?.description || '',
    parent_id: category?.parent_id || null,
    is_active: category?.is_active ?? true
  });
  const [categories, setCategories] = useState<any[]>([]);
  
  useEffect(() => {
    loadCategories();
  }, []);

  useEffect(() => {
    if (resetForm && !category) {
      setFormData({
        name: '',
        description: '',
        parent_id: null,
        is_active: true
      });
    } else if (category) {
      setFormData({
        name: category.name,
        description: category.description,
        parent_id: category.parent_id || null,
        is_active: category.is_active
      });
    }
  }, [category, resetForm]);

  const loadCategories = async () => {
    try {
      const response = await inventoryService.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleParentCategoryChange = (value: string | number | (string | number)[]) => {
    const parentId = Array.isArray(value) ? value[0] : value;
    setFormData(prev => ({
      ...prev,
      parent_id: parentId ? Number(parentId) : null
    }));
  };

  return (
    <>
      {!isCollapsed && (
        <form onSubmit={handleSubmit} className="erp-form-body">
          <div className="grid grid-cols-1 sm:grid-cols-6 erp-form-grid">
            <div>
              <label className="erp-form-label">Category Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="erp-form-input"
              />
            </div>

            <div>
              <label className="erp-form-label">Parent Category</label>
              <SearchableDropdown
                options={categories.filter(cat => cat.id !== category?.id).map(cat => ({
                  value: cat.id,
                  label: cat.name
                }))}
                value={formData.parent_id || ''}
                onChange={handleParentCategoryChange}
                placeholder="Select parent category..."
                multiple={false}
                searchable={true}
                className="w-full"
              />
            </div>

            <div>
              <label className="erp-form-label">Description</label>
              <input
                type="text"
                name="description"
                value={formData.description}
                onChange={handleChange}
                className="erp-form-input"
              />
            </div>

            <div>
              <label className="erp-form-label">Status</label>
              <select
                name="is_active"
                value={formData.is_active ? 'true' : 'false'}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                className="erp-form-input"
              >
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-end" style={{ gap: 'var(--erp-spacing-sm)' }}>
            <button
              type="button"
              onClick={onCancel}
              className="erp-form-btn text-gray-700 bg-gray-100 hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="erp-form-btn text-white bg-primary hover:bg-secondary"
            >
              {category ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      )}
    </>
  );
};

export default CategoryForm;